import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import Header from "../components/Header";
import Footer from "../components/Footer";
import AuthorBox from "../components/AuthorBox";
import SEOHead from "../components/SEOHead";
import { useLanguage } from "../contexts/LanguageContext";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';
import { 
  ArrowRight, 
  Settings,
  Rocket,
  Sprout,
  Clock,
  Target,
  TrendingUp,
  CheckCircle,
  Zap,
  BarChart3,
  Calculator
} from "lucide-react";

const ROIBusinessImpact = () => {
  const { currentLanguage } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [showSticky, setShowSticky] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    
    const handleScroll = () => {
      setShowSticky(window.scrollY > 600);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Chart Data - Cost of Inaction
  const chartData = [
    { month: 'Start', zoegern: 0, valueEngine: 0 },
    { month: 'M1', zoegern: -5, valueEngine: 15 },
    { month: 'M2', zoegern: -10, valueEngine: 35 },
    { month: 'M3', zoegern: -18, valueEngine: 60 },
    { month: 'M4', zoegern: -25, valueEngine: 95 },
    { month: 'M5', zoegern: -32, valueEngine: 140 },
    { month: 'M6', zoegern: -40, valueEngine: 200 },
    { month: 'M7', zoegern: -48, valueEngine: 270 },
    { month: 'M8', zoegern: -55, valueEngine: 350 },
    { month: 'M9', zoegern: -62, valueEngine: 440 },
    { month: 'M10', zoegern: -70, valueEngine: 540 },
    { month: 'M11', zoegern: -78, valueEngine: 650 },
    { month: 'M12', zoegern: -85, valueEngine: 780 },
  ];

  // 3 Business Impact Levers
  const businessLevers = [
    {
      icon: Settings,
      title: 'Operative Exzellenz',
      subtitle: 'Kostensenkung',
      description: 'Kostensenkung durch Automatisierung repetitiver Prozesse.',
      target: '-20% bis -40%',
      targetLabel: 'Prozesskosten',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Rocket,
      title: 'Umsatz-Beschleunigung',
      subtitle: 'Revenue Growth',
      description: 'KI-gestütztes Pricing & Lead-Scoring für höhere Abschlussraten.',
      target: '+10% bis +15%',
      targetLabel: 'Conversion-Rate',
      color: 'from-brand to-green-500'
    },
    {
      icon: Sprout,
      title: 'Asset-Wertsteigerung',
      subtitle: 'Exit-Readiness',
      description: 'Erhöhung der Exit-Readiness durch strukturierte Datenarchitektur.',
      target: 'PE-Fokus',
      targetLabel: 'Multiple-Steigerung',
      color: 'from-purple-500 to-pink-500'
    }
  ];

  // 12-Month Roadmap
  const roadmapPhases = [
    {
      phase: 'Phase 1',
      timeline: 'Tag 1-30',
      title: 'Quick-Check & Value-Thesen',
      description: 'Quick Wins identifizieren, Portfolio-Priorisierung, KPI-Baselines definieren',
      icon: Target,
      color: 'bg-blue-500'
    },
    {
      phase: 'Phase 2',
      timeline: 'Tag 31-90',
      title: 'Implementierung & Pilotierung',
      description: 'Erste Use Cases umsetzen, MVP-Tests, erster messbarer Impact',
      icon: Zap,
      color: 'bg-brand'
    },
    {
      phase: 'Phase 3',
      timeline: 'Tag 180+',
      title: 'Skalierung & Live-Dashboard',
      description: 'Rollout über Bereiche, Value-Dashboard live, volle EBIT-Wirkung',
      icon: TrendingUp,
      color: 'bg-green-500'
    }
  ];

  // Proof Points
  const proofPoints = [
    {
      metric: '42h',
      label: 'Zeitersparnis/Woche',
      context: 'Handel'
    },
    {
      metric: '<12',
      label: 'Monate bis ROI',
      context: 'PE-Portfolio'
    },
    {
      metric: '-40%',
      label: 'Support-Tickets',
      context: 'Service'
    }
  ];

  const seoData = {
    title: 'ROI & Business Impact | KI-Investitionen messbar machen - BROVKO',
    description: 'Verwandeln Sie KI-Investitionen in unternehmerischen Erfolg. Planbare und messbare Business Impact durch die KI-Value-Engine.',
    keywords: 'ROI KI, Business Impact, KI-Investitionen, EBIT Impact, KI Transformation ROI',
    canonicalUrl: '/roi-business-impact'
  };

  // Custom Tooltip for Chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="theme-brand bg-surface text-ink bg-surface-raised border border-hairline rounded-lg p-3 shadow-xl">
          <p className="text-ink-faint text-sm mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className={`text-sm font-semibold ${entry.dataKey === 'valueEngine' ? 'text-brand-400' : 'text-red-400'}`}>
              {entry.dataKey === 'valueEngine' ? 'KI-Value-Engine' : 'Zögern'}: {entry.value > 0 ? '+' : ''}{entry.value}k €
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-surface">
      <SEOHead {...seoData} />
      <Header />

      {/* Hero Section */}
      <section className="relative pt-40 pb-12 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-72 h-72 bg-brand rounded-full mix-blend-multiply filter blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Left - Text */}
            <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <h1 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-white mb-4 leading-tight">
                KI-Investitionen in <span className="bg-brand-accent bg-clip-text text-transparent">unternehmerischen Erfolg</span> verwandeln.
              </h1>

              <p className="text-lg text-ink-muted mb-6 leading-relaxed">
                Schluss mit dem Prinzip Hoffnung. Wir machen den Business Impact Ihrer KI-Transformation <strong className="text-white">planbar</strong> und <strong className="text-white">messbar</strong>.
              </p>

              <Link to="/ki-value-check">
                <Button 
                  size="lg"
                  className="bg-brand hover:bg-brand-600 text-ink-onBrand px-6 py-3 font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
                >
                  <Calculator className="mr-2 h-4 w-4" />
                  Quick-Check: Ihr ROI-Potenzial
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>

            {/* Right - Interactive Chart */}
            <div className={`transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="bg-surface-raised/50 backdrop-blur border border-hairline rounded-xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white font-semibold text-sm">Cost of Inaction</h3>
                  <span className="text-ink-faint text-xs">Kumulierter Wert (Tsd. €)</span>
                </div>
                
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={100}>
                    <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#14b8a6" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorZoegern" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="month" stroke="#64748b" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                      <YAxis stroke="#64748b" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area 
                        type="monotone" 
                        dataKey="valueEngine" 
                        stroke="#14b8a6" 
                        strokeWidth={2}
                        fill="url(#colorValue)"
                        name="KI-Value-Engine"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="zoegern" 
                        stroke="#ef4444" 
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        fill="url(#colorZoegern)"
                        name="Zögern"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                <div className="flex items-center justify-center gap-6 mt-3 pt-3 border-t border-hairline">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-1 bg-brand rounded"></div>
                    <span className="text-ink-faint text-xs">KI-Value-Engine</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-1 bg-red-500 rounded border-dashed"></div>
                    <span className="text-ink-faint text-xs">Zögern</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Logic Section */}
      <section className="py-20 md:py-28 bg-surface-sunken">
        <div className="container mx-auto px-4">
          <div className="max-w-[64ch] mb-10">
            <span className="inline-block bg-brand/10 text-brand-300 border border-brand-400/25 px-3 py-1.5 rounded-full text-xs font-medium mb-3">
              Die Business-Logik
            </span>
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-3">
              Die drei Hebel des Business Impact
            </h2>
            <p className="text-base text-ink-muted max-w-[62ch]">
              Jeder KI-Einsatz muss auf mindestens einen dieser Hebel einzahlen – sonst fehlt der Business Case.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {businessLevers.map((lever, index) => (
              <div 
                key={index}
                className="bg-surface rounded-xl p-6 shadow-lg border border-hairline hover:shadow-xl hover:border-brand-400/25 transition-all duration-300 group"
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${lever.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <lever.icon className="h-6 w-6 text-white" />
                </div>

                <div className="mb-3">
                  <span className="text-xs text-ink-faint uppercase tracking-wide">{lever.subtitle}</span>
                  <h3 className="text-lg font-bold text-ink">{lever.title}</h3>
                </div>

                <p className="text-ink-muted text-sm mb-4">
                  {lever.description}
                </p>

                <div className="pt-4 border-t border-hairline">
                  <div className="flex items-end justify-between">
                    <div>
                      <span className="text-xs text-ink-faint">Ziel</span>
                      <p className="text-xl font-bold text-brand-400">{lever.target}</p>
                    </div>
                    <span className="text-xs text-ink-faint">{lever.targetLabel}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roadmap Section */}
      <section className="py-20 md:py-28 bg-surface">
        <div className="container mx-auto px-4">
          <div className="max-w-[64ch] mb-10">
            <span className="inline-block bg-blue-500/15 text-blue-300 px-3 py-1.5 rounded-full text-xs font-medium mb-3">
              Der Weg zum Payback
            </span>
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-3">
              Die 12-Monats-Roadmap
            </h2>
            <p className="text-base text-ink-muted max-w-[62ch]">
              Von der ersten Analyse bis zum messbaren EBIT-Impact – strukturiert und ohne Umwege.
            </p>
          </div>

          {/* Roadmap Timeline */}
          <div className="max-w-5xl mx-auto">
            <div className="relative">
              {/* Connection Line */}
              <div className="hidden md:block absolute top-16 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-teal-500 to-green-500 rounded-full"></div>

              <div className="grid md:grid-cols-3 gap-6">
                {roadmapPhases.map((phase, index) => (
                  <div key={index} className="relative">
                    {/* Phase Circle */}
                    <div className="flex justify-center mb-4">
                      <div className={`w-12 h-12 ${phase.color} rounded-full flex items-center justify-center shadow-lg relative z-10`}>
                        <phase.icon className="h-6 w-6 text-white" />
                      </div>
                    </div>

                    {/* Phase Content */}
                    <div className="bg-surface-sunken rounded-xl p-5 text-center hover:shadow-lg transition-shadow duration-300">
                      <span className="inline-block bg-white/[0.09] text-ink-muted px-2 py-1 rounded-full text-xs font-semibold mb-2">
                        {phase.timeline}
                      </span>
                      <h3 className="text-lg font-bold text-ink mb-2">
                        {phase.title}
                      </h3>
                      <p className="text-ink-muted text-sm">
                        {phase.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Proof Points Section */}
      <section className="py-20 md:py-28 bg-surface">
        <div className="container mx-auto px-4">
          <div className="max-w-[64ch] mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">
              Bewiesene Ergebnisse
            </h2>
            <p className="text-ink-faint text-sm">Anonymisierte Referenzwerte aus Kundenprojekten</p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {proofPoints.map((point, index) => (
              <div 
                key={index}
                className="bg-surface-raised/50 backdrop-blur border border-hairline rounded-lg p-5 text-center hover:border-teal-500/50 transition-all duration-300"
              >
                <p className="text-4xl font-bold text-brand-400 mb-1">{point.metric}</p>
                <p className="text-white font-semibold text-sm mb-1">{point.label}</p>
                <p className="text-ink-faint text-xs">{point.context}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Benefits Section */}
      <section className="py-20 md:py-28 bg-surface-sunken">
        <div className="container mx-auto px-4">
          <div className="max-w-[64ch] mb-8">
            <span className="inline-block bg-purple-500/15 text-purple-300 px-3 py-1.5 rounded-full text-xs font-medium mb-3">
              Für wen?
            </span>
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-3">
              Wer profitiert am meisten?
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-4 max-w-5xl mx-auto">
            <div className="bg-surface rounded-lg p-5 shadow-lg border-l-4 border-brand">
              <h3 className="text-lg font-bold text-ink mb-2">Mittelstand</h3>
              <p className="text-ink-muted text-sm mb-3">
                Unternehmen mit 50-500 MA, die KI strategisch angehen wollen – ohne eigene Data-Science-Abteilung.
              </p>
              <div className="flex items-center gap-2 text-brand-400 text-xs font-medium">
                <CheckCircle className="h-3.5 w-3.5" />
                <span>Schnelle Quick Wins</span>
              </div>
            </div>

            <div className="bg-surface rounded-lg p-5 shadow-lg border-l-4 border-blue-500">
              <h3 className="text-lg font-bold text-ink mb-2">PE-Portfolio</h3>
              <p className="text-ink-muted text-sm mb-3">
                Portfolio-Companies, die vor einem Exit die Technologie-Readiness nachweisen müssen.
              </p>
              <div className="flex items-center gap-2 text-blue-400 text-xs font-medium">
                <CheckCircle className="h-3.5 w-3.5" />
                <span>Exit-Vorbereitung</span>
              </div>
            </div>

            <div className="bg-surface rounded-lg p-5 shadow-lg border-l-4 border-purple-500">
              <h3 className="text-lg font-bold text-ink mb-2">Konzerne</h3>
              <p className="text-ink-muted text-sm mb-3">
                Geschäftsbereiche, die aus der "Pilotitis" herauskommen und skalieren wollen.
              </p>
              <div className="flex items-center gap-2 text-purple-400 text-xs font-medium">
                <CheckCircle className="h-3.5 w-3.5" />
                <span>Skalierbare Strukturen</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section - MORE PROMINENT */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-teal-600 to-cyan-600">
        <div className="container mx-auto px-4">
          <div className="max-w-[62ch] text-center">
            <Calculator className="h-10 w-10 text-white/80 mx-auto mb-4" />
            
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-4">
              Wie viel Wertpotenzial steckt in Ihrem Unternehmen?
            </h2>

            <p className="text-lg text-white/90 mb-8">
              In 5 Minuten erfahren Sie, wo Ihre größten Hebel liegen – kostenlos und unverbindlich.
            </p>

            <Link to="/ki-value-check">
              <Button 
                size="lg"
                className="bg-surface text-brand-400 hover:bg-surface-raised px-8 py-4 text-lg font-bold rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 group animate-pulse hover:animate-none"
              >
                Jetzt ROI-Analyse starten
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Sticky Lead Magnet */}
      {showSticky && (
        <div className="fixed bottom-6 right-6 z-40 animate-in slide-in-from-bottom-5 duration-300">
          <div className="bg-surface border border-hairline rounded-xl p-5 shadow-2xl max-w-xs">
            <p className="text-white font-semibold mb-2">ROI-Potenzial berechnen</p>
            <p className="text-ink-faint text-sm mb-4">5 Minuten. Kostenlos. Unverbindlich.</p>
            <Link to="/ki-value-check">
              <Button className="w-full bg-brand hover:bg-brand-600 text-ink-onBrand">
                Jetzt prüfen
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4">
        <AuthorBox />
      </div>

      <Footer />
    </div>
  );
};

export default ROIBusinessImpact;
